/** 创新评价模块 — 路由映射（对齐思维导图结构；顶部 Tab 见 InnovationTopNav） */

export type ReviewKind = "project" | "patent" | "paper";

export type NavLeaf = {
  id: string;
  label: string;
  path: string;
};

export type NavBranch = {
  id: string;
  label: string;
  icon?: string;
  children: (NavLeaf | NavBranch)[];
};

export const innovationNavTree: NavBranch[] = [
  {
    id: "topic",
    label: "选题",
    children: [
      { id: "topic-gen", label: "选题生成", path: "/innovation/topic/generation" },
      { id: "topic-eval", label: "选题评估", path: "/innovation/topic/evaluation" },
    ],
  },
  {
    id: "review",
    label: "评审",
    children: [
      {
        id: "review-project",
        label: "项目评审",
        children: [
          { id: "pj-key", label: "关键信息提取", path: "/innovation/review/project/key-info" },
          { id: "pj-cmp", label: "对比信息搜索", path: "/innovation/review/project/compare-search" },
          { id: "pj-imp", label: "项目重要性", path: "/innovation/review/project/importance" },
          { id: "pj-dup-t", label: "选题重复性", path: "/innovation/review/project/topic-repeat" },
          { id: "pj-adv", label: "指标先进性", path: "/innovation/review/project/indicator-advanced" },
          { id: "pj-fund", label: "经费合理性", path: "/innovation/review/project/funding" },
          { id: "pj-comp", label: "竞争优势单位", path: "/innovation/review/project/competitive" },
          { id: "pj-report", label: "项目评审综合报告", path: "/innovation/review/project/report" },
        ],
      },
      {
        id: "review-patent",
        label: "专利评审",
        children: [
          { id: "pt-key", label: "关键信息提取", path: "/innovation/review/patent/key-info" },
          { id: "pt-cmp", label: "对比信息搜索", path: "/innovation/review/patent/compare-search" },
          { id: "pt-dup", label: "专利重复性", path: "/innovation/review/patent/repeatability" },
          { id: "pt-adv", label: "指标先进性", path: "/innovation/review/patent/indicator-advanced" },
          { id: "pt-fmt", label: "格式规范性", path: "/innovation/review/patent/format" },
          { id: "pt-report", label: "专利评审综合报告", path: "/innovation/review/patent/report" },
        ],
      },
      {
        id: "review-paper",
        label: "论文评审",
        children: [
          { id: "pp-key", label: "关键信息提取", path: "/innovation/review/paper/key-info" },
          { id: "pp-cmp", label: "对比信息搜索", path: "/innovation/review/paper/compare-search" },
          { id: "pp-dup", label: "论文重复性", path: "/innovation/review/paper/repeatability" },
          { id: "pp-adv", label: "指标先进性", path: "/innovation/review/paper/indicator-advanced" },
          { id: "pp-fmt", label: "格式规范性", path: "/innovation/review/paper/format" },
          { id: "pp-lang", label: "语言专业性", path: "/innovation/review/paper/language" },
          { id: "pp-report", label: "论文评审综合报告", path: "/innovation/review/paper/report" },
        ],
      },
    ],
  },
  {
    id: "suggest",
    label: "建议",
    children: [
      { id: "sg-inv", label: "创新建议", path: "/innovation/suggestions/innovation" },
      { id: "sg-ind", label: "指标建议", path: "/innovation/suggestions/indicator" },
      { id: "sg-met", label: "方法建议", path: "/innovation/suggestions/method" },
      { id: "sg-fund", label: "经费建议", path: "/innovation/suggestions/funding" },
      { id: "sg-diff", label: "差异化建议", path: "/innovation/suggestions/differentiation" },
      { id: "sg-journal", label: "投稿选刊建议", path: "/innovation/suggestions/journal" },
      { id: "sg-reply", label: "审稿回复建议", path: "/innovation/suggestions/review-reply" },
      { id: "sg-sum", label: "综合建议", path: "/innovation/suggestions/comprehensive" },
    ],
  },
  {
    id: "records",
    label: "评价记录",
    children: [
      { id: "rec-list", label: "记录详情", path: "/innovation/records" },
      {
        id: "rec-share",
        label: "记录分享",
        children: [
          { id: "rec-share-link", label: "分享链接", path: "/innovation/records/share/link" },
          { id: "rec-share-kb", label: "分享到知识库", path: "/innovation/records/share/knowledge" },
        ],
      },
    ],
  },
];

export function flattenNodes(nodes: (NavLeaf | NavBranch)[]): NavLeaf[] {
  const out: NavLeaf[] = [];
  for (const n of nodes) {
    if ("path" in n && typeof (n as NavLeaf).path === "string") {
      out.push(n as NavLeaf);
    } else if ("children" in n) {
      out.push(...flattenNodes((n as NavBranch).children));
    }
  }
  return out;
}

export function flattenLeaves(branches: NavBranch[]): NavLeaf[] {
  return flattenNodes(branches as unknown as (NavLeaf | NavBranch)[]);
}

export function getTitleForPath(pathname: string): string {
  if (pathname === "/innovation" || pathname === "/innovation/") return "概览";
  const leaves = flattenLeaves(innovationNavTree).sort((a, b) => b.path.length - a.path.length);
  const hit = leaves.find((l) => pathname === l.path || pathname.startsWith(`${l.path}/`));
  return hit?.label ?? "创新评价";
}
